import React, { useEffect, useRef, useState } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { InfinitePagination } from '$partials/base/ajax-listing.jsx';

import { ArrowRightIcon, CloseIcon } from '$partials/icon';
import { Map, MapCanvas } from '$partials/base/map.jsx';
import { Button } from '$partials/ui';
import { Image } from '$partials/media';
import { formatPrice, formatRentFrequency } from '$utilities/format';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modals.d.ts').MapFilterModal}
 */
const MapFilterModal = ({
    filters,
    resetFilters,
    listing,
    isNavOpen,
    handleClose,
    ...rest
}) => {
    const dialogRef = useRef();
    const scrollElementRef = useRef();
    const [ activeItem, setActiveItem ] = useState('');
    const [ itemLocations, setItemLocations ] = useState(listing.items?.filter(item=> !item.node.offMarket)?.map(item => {
        return {
            lat: item.node.mapLatitude,
            lng: item.node.mapLongitude,
            id: item.node.id
        };
    }));


    useEffect(() => {
        const locations = listing.items?.filter(item=> !item.node.offMarket)?.map(item => {
            return {
                lat: item.node.mapLatitude,
                lng: item.node.mapLongitude,
                id: item.node.id
            };
        });
        setItemLocations(locations);
    }, [listing.items.length]);

    useEffect(() => {
        if (document?.querySelector && activeItem) {
            document.querySelector(`[data-sm-map-list-key="${activeItem}"]`).scrollIntoView({ behavior: 'smooth' });
            document.querySelector(`[data-md-map-list-key="${activeItem}"]`).scrollIntoView({behavior: 'smooth'});
        }
    }, [activeItem]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        const scrollElementNode = scrollElementRef.current;
        if (isNavOpen) {
            if (dialogNode && !dialogNode.open && scrollElementNode) {
                disableBodyScroll(scrollElementNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open && scrollElementNode) {
                enableBodyScroll(scrollElementNode);
                dialogNode.close?.();
            }
        }

        return () => {
            enableBodyScroll(scrollElementNode);
        };
    }, [isNavOpen]);

    return (
        <dialog
            className='dialog-reset'
            onClick={handleClose}
            ref={dialogRef}>
            <div
                className={'overflow-y-scroll fixed top-0 bottom-0 left-0 h-dvh filter-dialog bg-light-charcoal'}
                /* `stopPropagation()` to not close dialog when dialog is clicked */
                onClick={(e) => e.stopPropagation()}
                ref={scrollElementRef}>
                <div className={'fixed left-0 top-0 height-100 width-100 border-0 m0 p0 overflow-hidden bg-grey z3 flex flex-wrap'}>
                    <div
                        className='md-hide absolute top-0 right right-0 z5 mt5 mr5'>
                        <Button
                            aria-label='Close'
                            as = 'button'
                            className = 'bg-black white p3 md-p5'
                            look = 'button'
                            onClick={handleClose}
                            renderIcon = {<CloseIcon />}/>

                    </div>
                    <div className='col-12 md-col-8 relative h-full'>
                        {itemLocations.length > 0 && (
                            <Map
                                {...rest}
                                activeItem={activeItem}
                                isClusterable={true}
                                lat={itemLocations?.[0]?.lat}
                                lng={itemLocations?.[0]?.lng}
                                locations={itemLocations}
                                setActiveItem={setActiveItem}>
                                <MapCanvas className='map-canvas' />
                            </Map>
                        )}
                        {listing?.page < listing?.totalPages && (
                            <div className='col-12 sm-hide absolute pointer-events-none top-0 height-100vh'>
                                <InfinitePagination>
                                    <div className='col-12 justify-center items-center height-100vh bg-grey flex'>
                                        <p className='type-style-5 px5 py7 black'>Loading</p>
                                    </div>
                                </InfinitePagination>
                            </div>
                        )}
                    </div>

                    {activeItem && (
                        <div className='expand-interaction-container sm-hide absolute bottom-0 bg-white py5'>
                            <div className='absolute right right-0 pr5'>
                                <Button
                                    aria-label='Close'
                                    as = 'button'
                                    className = 'bg-black white p3'
                                    look = 'button'
                                    onClick={() => {
                                        setActiveItem('');
                                    }}
                                    renderIcon = {<CloseIcon />} />
                            </div>
                            {listing.items.filter(item => item.node.id === activeItem).map(item => {
                                const image = item.node?.listingMedia?.[0]?.image;
                                const price = item.node.__typename === 'PropertyPage' ? formatPrice(item.node.priceGbp) : `${formatPrice(item.node.rentGbp)} ${formatRentFrequency(item.node.rentFrequency)}`;
                                return (
                                    <a
                                        className='link-reset expand-interaction__action flex flex-col pl5 pr13'
                                        href={item.node.url}
                                        key={item.node.id}>
                                        {image &&
                                            <div style={{ width: image.width / image.height * 170 }}>
                                                <Image {...image} />
                                            </div>
                                        }
                                        <div className='black width-100 pt5'>
                                            <p className='type-style-5'>
                                                {price}
                                            </p>
                                            <h4 className='type-style-4 mb3'>
                                                {item.node.heading}
                                            </h4>
                                            <p className='type-style-5'>
                                                {item.node.propertyName}
                                            </p>
                                            <p className='type-style-5'>
                                                {item.node.area}{' '}{item.node.outwardCode}
                                            </p>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    )}

                    {/* Tablet */}
                    <div className='hide sm-show md-hide col-12 map-info bg-white'>
                        <div className='flex width-100 height-100 overflow-auto'>
                            {listing?.items?.length > 0 ? (
                                <>
                                    {/* Listing */}
                                    {listing.items.filter(item=> !item.node.offMarket).map(item => {
                                        const image = item.node?.listingMedia?.[0]?.image;
                                        const price = item.node.__typename === 'PropertyPage' ? formatPrice(item.node.priceGbp) : `${formatPrice(item.node.rentGbp)} ${formatRentFrequency(item.node.rentFrequency)}`;

                                        return (
                                            <div
                                                className={`expand-interaction-container border-right border-left grey cursor-pointer${activeItem === item.node.id ? ' bg-yellow': ''}`}
                                                data-sm-map-list-key={item.node.id}
                                                key={item.node.id}>
                                                <a
                                                    className='link-reset expand-interaction__action flex flex-wrap col-6 py5 pl5 pr12 '
                                                    href={item.node.url}
                                                    onClick={(e) => {
                                                        if (activeItem !== item.node.id) {
                                                            e.preventDefault();
                                                            setActiveItem(item.node.id);
                                                        }
                                                    }}>
                                                    {image && (
                                                        <div
                                                            className='pb5'
                                                            style={{fontSize: '140px'}}>
                                                            <div style={{
                                                                width: `${(image.width / image.height)}em`
                                                            }}>
                                                                <Image {...image} />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div
                                                        className='black width-100'
                                                        style={{minWidth: '225px'}}>
                                                        <p className='type-style-5'>
                                                            {price}
                                                        </p>
                                                        <h4 className='type-style-4 mb3'>
                                                            {item.node.heading}
                                                        </h4>
                                                        <p className='type-style-5'>
                                                            {item.node.propertyName}
                                                        </p>
                                                        <p className='type-style-5'>
                                                            {item.node.area}{' '}{item.node.outwardCode}
                                                        </p>
                                                    </div>
                                                </a>
                                            </div>
                                        );
                                    })}</>
                            ) :
                                <div className='col-12 md-col-4 py9 px5 md-px6'>
                                    <p className='type-style-5 mb3 black'>Please choose different filters</p>
                                    <h2 className='type-style-2 black'>No Properties Found</h2>
                                    <div className='col-12 md-col-4'>
                                        <Button
                                            as='button'
                                            className='bg-none type-style-5'
                                            iconClassName='bg-black white button border-radius-100px p3 md-p5'
                                            look='underline'
                                            onClick={() => {
                                                resetFilters();
                                            }}
                                            renderIcon={<ArrowRightIcon/>}>
                                        Reset Filters
                                        </Button>
                                    </div>
                                </div>
                            }
                            {listing?.page < listing?.totalPages &&
                            <InfinitePagination>
                                <div>
                                    <p className='type-style-5 px5 py5 black'>Loading</p>
                                </div>
                            </InfinitePagination>
                            }
                        </div>
                    </div>

                    {/* Desktop */}
                    <div className='hide md-show col-12 md-col-4 bg-white height-100'>
                        <div className='flex flex-wrap md-flex-col height-100'>
                            {/* Modal controls */}
                            <div className='col-12 border-bottom grey hide md-show flex-row'>
                                <div className='md-flex justify-end'>
                                    <Button
                                        aria-label='Close'
                                        as='button'
                                        className='bg-white p3 md-p5 md-my3'
                                        look='button'
                                        onClick={handleClose}
                                        renderIcon={<CloseIcon/>}/>
                                </div>
                            </div>

                            <div className='col-12 border-right grey md-border-0 flex-1 height-100 overflow-y-auto'>
                                {listing?.items?.length > 0 ? (
                                    <>
                                        {/* Listing */}
                                        {listing.items.filter(item=> !item.node.offMarket).map(item => {
                                            const image = item.node?.listingMedia?.[0]?.image;
                                            const price = item.node.__typename === 'PropertyPage' ? formatPrice(item.node.priceGbp) : `${formatPrice(item.node.rentGbp)} ${formatRentFrequency(item.node.rentFrequency)}`;

                                            return (
                                                <div
                                                    className={`expand-interaction-container border-top border-bottom grey hover-bg-yellow cursor-pointer${activeItem === item.node.id ? ' bg-yellow': ''}`}
                                                    data-md-map-list-key={item.node.id}
                                                    key={`desktop-${item.node.id}`}>
                                                    <a
                                                        className='link-reset expand-interaction__action py5 px6 flex'
                                                        href={item.node.url}
                                                        onClick={(e) => {
                                                            if (activeItem !== item.node.id) {
                                                                e.preventDefault();
                                                                setActiveItem(item.node.id);
                                                            }
                                                        }}>
                                                        {image && (
                                                            <div style={{width: image.width / image.height * 170 }}>
                                                                <Image {...image} />
                                                            </div>
                                                        )}
                                                        <div className='black pl6 width-100'>
                                                            <p className='type-style-5'>
                                                                {price}
                                                            </p>
                                                            <h4 className='type-style-4 mb3'>
                                                                {item.node.heading}
                                                            </h4>
                                                            <p className='type-style-5'>
                                                                {item.node.propertyName}
                                                            </p>
                                                            <p className='type-style-5'>
                                                                {item.node.area}{' '}{item.node.outwardCode}
                                                            </p>
                                                        </div>
                                                    </a>
                                                </div>
                                            );
                                        })}
                                    </>) : (
                                    <div className='col-12 py9 px5 md-px6'>
                                        <p className='type-style-5 mb3 black'>Please choose different filters</p>
                                        <h2 className='type-style-2 black'>No Properties Found</h2>
                                        <div className='col-12'>
                                            <Button
                                                as='button'
                                                className='bg-none type-style-5'
                                                iconClassName='bg-black white button border-radius-100px p3 md-p5'
                                                look='underline'
                                                onClick={() => {
                                                    resetFilters();
                                                }}
                                                renderIcon = {<ArrowRightIcon />}>
                                        Reset Filters
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {listing?.page < listing?.totalPages &&
                            <InfinitePagination>
                                <div>
                                    <p className='type-style-5 px6 py7 black'>Loading</p>
                                </div>
                            </InfinitePagination>
                                }
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </dialog>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default MapFilterModal ;
